import React, { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import styles from "./homeHero.module.css";
import { Button } from "../../../../COMPONENTS/UI";
import { hero } from "../../../../CONTENT/home";
import heroImage from "../../../../ASSETS/BACKGROUND/KURpic1-2-scaled.jpg";
import gtrImage from "../../../../ASSETS/BACKGROUND/GTR_seal.png";
import { EASE } from "../../../../UTILS/ANIMATION/motionVariants";

const LINES = ["Making", "progress", "possible."];
const SPRING = { stiffness: 120, damping: 22, mass: 0.6 };

const Seal = () => (
  <>
    <img src={gtrImage} alt="Gene Therapy Ready seal" width="56" height="56" />
    <p>{hero.seal}</p>
  </>
);

const HeroSection = () => {
  const reduce = useReducedMotion();
  const heroRef = useRef(null);

  // Pointer position across the hero, -0.5..0.5, eased with a spring.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, SPRING);
  const sy = useSpring(py, SPRING);

  // Scroll progress of the hero leaving the viewport.
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });

  // Photo: small cursor drift plus a slower-than-page scroll. The image is
  // rendered at 1.06x so these offsets never expose the mask edge.
  const photoX = useTransform(sx, (v) => v * -16);
  const photoY = useTransform([sy, scrollYProgress], ([v, p]) => v * -12 + p * 14);
  // Ambient glow trails the cursor behind the copy.
  const glowX = useTransform(sx, (v) => v * 90);
  const glowY = useTransform(sy, (v) => v * 70);
  // Copy eases up and dims as the hero scrolls away.
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.25]);

  const onPointerMove = (e) => {
    if (reduce || e.pointerType !== "mouse") return;
    const r = heroRef.current.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onPointerLeave = () => {
    px.set(0);
    py.set(0);
  };

  const rise = (delay) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 22, filter: "blur(8px)" },
          animate: { opacity: 1, y: 0, filter: "blur(0px)", transitionEnd: { filter: "none" } },
          transition: { duration: 0.9, delay, ease: EASE },
        };

  return (
    <section
      ref={heroRef}
      className={styles.hero}
      aria-labelledby="home-hero-title"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      <motion.span
        className={styles.glow}
        aria-hidden="true"
        style={reduce ? undefined : { x: glowX, y: glowY }}
      />

      <div className={styles.inner}>
        <motion.div className={styles.copy} style={reduce ? undefined : { y: copyY, opacity: copyOpacity }}>
          <h1 id="home-hero-title" className={styles.title} aria-label="Making progress possible.">
            {LINES.map((line, i) => (
              <span key={line} className={styles.line} aria-hidden="true">
                <motion.span
                  className={`${styles.lineInner} ${i === LINES.length - 1 ? styles.accent : ""}`}
                  initial={reduce ? false : { y: "108%", rotate: 2.5 }}
                  animate={{ y: "0%", rotate: 0 }}
                  transition={{ duration: 1.05, delay: 0.25 + i * 0.12, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p className={styles.lead} {...rise(0.7)}>
            {hero.lead}
          </motion.p>

          <motion.div className={styles.actions} {...rise(0.85)}>
            <Button href={hero.primary.href} variant="primary">
              {hero.primary.label}
            </Button>
            <Button href={hero.secondary.href} variant="secondary" tone="dark">
              {hero.secondary.label}
            </Button>
            <span className={styles.break} aria-hidden="true" />
            <Button href={hero.tertiary.href} variant="link" tone="dark" className={styles.textCta}>
              {hero.tertiary.label}
            </Button>
          </motion.div>

          <motion.div className={styles.seal} {...rise(1)}>
            <Seal />
          </motion.div>
        </motion.div>

        <div className={styles.visual}>
          <span className={styles.arc} aria-hidden="true" />
          <div className={styles.mask}>
            <motion.div className={styles.drift} style={reduce ? undefined : { x: photoX, y: photoY }}>
              <motion.img
                className={styles.photo}
                src={heroImage}
                alt="A KUR clinician in protective equipment holding a vial"
                fetchpriority="high"
                initial={reduce ? false : { scale: 1.16 }}
                animate={{ scale: 1.06 }}
                transition={{ duration: 1.8, delay: 0.05, ease: EASE }}
              />
            </motion.div>
            {!reduce && (
              <motion.span
                className={styles.sheen}
                aria-hidden="true"
                initial={{ x: "0%" }}
                animate={{ x: "340%" }}
                transition={{ duration: 1.6, delay: 1.05, ease: [0.45, 0, 0.2, 1] }}
              />
            )}
          </div>
          <motion.div
            className={styles.badge}
            initial={reduce ? false : { opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.25, ease: EASE }}
          >
            <Seal />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
